export const SCRAMBLE_CHARSET = 'abcdefghijklmnopqrstuvxyz/-!=+"~*&0123456789';
export const SCRAMBLE_CHARSET_UPPER = 'ABCDEFGHKLNOPRSTUVXYZ';
export const REVEAL_CHARSET = 'abcdefghijklmnopqrstuvwxyz';

const AMBIENT_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVXYZ';

const LETTER = /[a-zA-Z]/;
const FRAME_MS = 50;                                // time between character swaps during a scramble, in milliseconds.
const AMBIENT_SELECTOR = '.letter-scrambler';       // CSS selector for elements to scramble
const SCRAMBLE_INTERVAL_MS = [3000, 3000] as const; // random pause between scrambles, in milliseconds
const SCRAMBLE_DURATION_MS = [1000, 1000] as const; // random duration of each scramble, in milliseconds
const AMBIENT_CONCURRENT = 1;                       // number of concurrent character scrambles
const ONE_SHOT_DURATION_MS = 4000;                  // duration of one-shot scramble, in milliseconds

export interface AmbientScrambleOptions {
  selector?: string;
  intervalMs?: readonly [number, number];
  durationMs?: readonly [number, number];
  frameMs?: number;
  charset?: string;
  concurrent?: number;
}

export interface ScrambleTextOptions {
  durationMs?: number;
  frameMs?: number;
  charset?: string;
  revealLeftToRight?: boolean;
  onDone?: () => void;
}

const pick = <T>(items: ArrayLike<T>): T => items[Math.floor(Math.random() * items.length)];

const randomBetween = ([min, max]: readonly [number, number]) => min + Math.random() * (max - min);

const matchCase = (letter: string, replacement: string) =>
  letter === letter.toUpperCase() ? replacement.toUpperCase() : replacement.toLowerCase();

const letterTextNodes = (el: Element): Text[] =>
  Array.from(el.childNodes).filter(
    (child): child is Text => child.nodeType === Node.TEXT_NODE && LETTER.test(child.nodeValue ?? ''),
  );

const letterIndices = (text: string): number[] =>
  Array.from(text, (char, index) => (LETTER.test(char) ? index : -1)).filter((index) => index >= 0);

const replaceAt = (text: string, index: number, char: string) => text.slice(0, index) + char + text.slice(index + 1);

export function startAmbientScramble(root: HTMLElement, options: AmbientScrambleOptions = {}): () => void {
  const {
    selector = AMBIENT_SELECTOR,
    intervalMs = SCRAMBLE_INTERVAL_MS,
    durationMs = SCRAMBLE_DURATION_MS,
    frameMs = FRAME_MS,
    charset = AMBIENT_CHARSET,
    concurrent = AMBIENT_CONCURRENT,
  } = options;
  const busy = new Set<Text>();
  // console.log(`selector: ${selector}, intervalMs: ${intervalMs}, durationMs: ${durationMs}, concurrent: ${concurrent}`)
  const runLoop = (): (() => void) => {
    let timeoutId = 0;
    let rafId = 0;
    let restore: (() => void) | null = null;

    const schedule = () => {
      const interval = randomBetween(intervalMs);
      // console.log(`scheduling next scramble in ${interval}ms`)
      timeoutId = window.setTimeout(tick, interval);
    };

    const tick = () => {
      // Get all text nodes that match the selector and are not busy
      const candidates = Array.from(root.querySelectorAll(selector), letterTextNodes)
        .map((nodes) => nodes.filter((node) => !busy.has(node)))
        .filter((nodes) => nodes.length > 0);
      
      // Schedule a retry if there are no available candidates
      if (candidates.length === 0) {
        schedule();
        return;
      }

      const node = pick(pick(candidates));
      busy.add(node);
      const text = node.nodeValue ?? '';
      const index = pick(letterIndices(text));
      const original = text[index];
      const scrambleDuration = randomBetween(durationMs);
      const endAt = performance.now() + scrambleDuration;
      let lastFrame = performance.now();
      let written = text;

      restore = () => {
        restore = null;
        busy.delete(node);
        window.cancelAnimationFrame(rafId);
        if (node.nodeValue === written) {
          node.nodeValue = replaceAt(written, index, original);
        }
      };

      const frame = (now: number) => {
        if (now >= endAt || !node.isConnected || node.nodeValue !== written) {
          // console.log(`character scramble ended. time: ${performance.now().toFixed(0)}`)
          restore?.();
          schedule();
          return;
        }
        if (now - lastFrame >= frameMs) {
          written = replaceAt(written, index, matchCase(original, pick(charset)));
          node.nodeValue = written;
          lastFrame = now;
        }
        rafId = window.requestAnimationFrame(frame);
      };

      // console.log(`starting character scramble for ${scrambleDuration}ms. time: ${performance.now().toFixed(0)}`)
      rafId = window.requestAnimationFrame(frame);
    };

    schedule();

    return () => {
      window.clearTimeout(timeoutId);
      restore?.();
    };
  };

  const cancels = Array.from({ length: Math.max(1, concurrent) }, runLoop);

  return () => {
    cancels.forEach((cancel) => cancel());
  };
}

export function scrambleTextOneShot(el: HTMLElement, options: ScrambleTextOptions = {}): () => void {
  const {
    durationMs = ONE_SHOT_DURATION_MS,
    frameMs = FRAME_MS,
    charset = SCRAMBLE_CHARSET,
    revealLeftToRight = false,
    onDone,
  } = options;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    nodes.push(node as Text);
  }
  const originals = nodes.map((node) => node.nodeValue ?? '');
  const written = [...originals];
  const nodeOffsets = originals.map((_, i) => originals.slice(0, i).reduce((sum, text) => sum + text.length, 0));
  const totalLength = originals.reduce((sum, text) => sum + text.length, 0);
  const startedAt = performance.now();
  let lastFrame = startedAt;
  let rafId = 0;

  const restore = () => {
    window.cancelAnimationFrame(rafId);
    nodes.forEach((node, i) => {
      if (node.nodeValue === written[i]) node.nodeValue = originals[i];
    });
  };

  const revealAt = (position: number) => (totalLength > 1 ? (position / (totalLength - 1)) * durationMs : 0);

  const scrambled = (text: string, offset: number, elapsed: number) =>
    revealLeftToRight
      ? Array.from(text, (char, i) => (elapsed >= revealAt(offset + i) ? char : pick(charset))).join('')
      : text.replace(/\S/gu, () => pick(charset));

  const write = (elapsed: number) => {
    nodes.forEach((node, i) => {
      if (node.nodeValue !== written[i]) return;
      written[i] = scrambled(originals[i], nodeOffsets[i], elapsed);
      node.nodeValue = written[i];
    });
  };

  const frame = (now: number) => {
    const elapsed = now - startedAt;
    if (elapsed >= durationMs) {
      restore();
      onDone?.();
      return;
    }
    if (now - lastFrame >= frameMs) {
      write(elapsed);
      lastFrame = now;
    }
    rafId = window.requestAnimationFrame(frame);
  };

  if (revealLeftToRight) write(0);
  rafId = window.requestAnimationFrame(frame);

  return restore;
}
